import { useMemo } from "react"

import {
  EvaluationConstructor,
  EvaluationProblemFlag,
} from "#customTypes/composedTypes/evaluationTypes"
import { Uuid } from "#customTypes/globalTypes"

type EvaluationProblemFlagSource = {
  flaggedEvalIds?: Uuid[]
  flagsByEvaluationId?: Record<Uuid, EvaluationProblemFlag>
}

const EMPTY_FLAG_MAP: Record<Uuid, EvaluationProblemFlag> = {}
const EMPTY_FLAGGED_IDS: Uuid[] = []

/**
 * Pluggable problem-flag adapter.
 * Currently criteria-free by design; future rules can provide ids/flags.
 */
const useEvaluationProblemFlags = ({
  evaluations,
  source,
}: {
  evaluations: EvaluationConstructor[]
  source?: EvaluationProblemFlagSource
}) => {
  return useMemo(() => {
    if (!source) {
      return {
        flagsByEvaluationId: EMPTY_FLAG_MAP,
        flaggedEvalIds: EMPTY_FLAGGED_IDS,
      }
    }

    const flaggedIdSet = new Set(source.flaggedEvalIds ?? [])
    const nextMap: Record<Uuid, EvaluationProblemFlag> = {}

    for (const evaluation of evaluations) {
      const keyedFlag = source.flagsByEvaluationId?.[evaluation.id]
      if (keyedFlag?.isProblematic) {
        nextMap[evaluation.id] = keyedFlag
        flaggedIdSet.add(evaluation.id)
      } else if (flaggedIdSet.has(evaluation.id)) {
        nextMap[evaluation.id] = {
          isProblematic: true,
          reasonCodes: [],
        }
      }
    }

    return {
      flagsByEvaluationId: nextMap,
      flaggedEvalIds: Array.from(flaggedIdSet),
    }
  }, [evaluations, source])
}

export default useEvaluationProblemFlags
